import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, Loader2, Save, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useQuery } from "../../lib/useQuery";
import { supabase } from "../../lib/supabase";
import { MONTHS_ABBR } from "../../lib/constants";
import { useToast } from "../Toast";
import LoadingSkeleton from "../LoadingSkeleton";

const inputClass =
  "w-full px-1 py-1 text-xs text-center border border-gray-200 rounded focus:border-brand focus:ring-1 focus:ring-brand/20 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

export default function ClientesMensuales({ onBack }) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [localClientes, setLocalClientes] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: monthlyData, loading, refetch } = useQuery(async () => {
    const { data } = await supabase
      .from("monthly_data")
      .select("*")
      .eq("student_id", user.id)
      .eq("year", year);
    return data || [];
  }, [user.id, year]);

  useEffect(() => {
    const map = {};
    monthlyData?.forEach((d) => {
      if (d.n_clientes !== null && d.n_clientes !== undefined) {
        map[d.month] = d.n_clientes;
      }
    });
    setLocalClientes(map);
  }, [monthlyData]);

  const getDisplayValue = useCallback(
    (month) => {
      for (let m = month; m >= 1; m--) {
        if (localClientes[m] !== undefined && localClientes[m] !== "") {
          return {
            value: localClientes[m],
            isReplicated: m !== month,
          };
        }
        const existing = monthlyData?.find((d) => d.month === m);
        if (existing?.n_clientes !== undefined && existing.n_clientes !== null) {
          return { value: existing.n_clientes, isReplicated: m !== month };
        }
      }
      return { value: 0, isReplicated: false };
    },
    [monthlyData, localClientes]
  );

  const handleChange = (month, value) => {
    setLocalClientes((prev) => ({ ...prev, [month]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (let month = 1; month <= 12; month++) {
        const { value } = getDisplayValue(month);
        let finalValue;
        if (localClientes[month] !== undefined && localClientes[month] !== "") {
          finalValue = parseInt(localClientes[month]) || 0;
        } else if (value) {
          finalValue = parseInt(value) || 0;
        } else {
          continue;
        }

        const existing = monthlyData?.find((d) => d.month === month);
        if (existing) {
          await supabase
            .from("monthly_data")
            .update({ n_clientes: finalValue })
            .eq("id", existing.id);
        } else {
          await supabase.from("monthly_data").insert({
            student_id: user.id,
            year,
            month,
            n_clientes: finalValue,
          });
        }
      }

      await refetch();
      showSuccess("Clientes guardados correctamente");
    } catch (err) {
      console.error(err);
      showError("No se pudieron guardar los datos");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="animate-fade-in">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6"
      >
        <ChevronLeft size={18} /> Volver a Herramientas
      </button>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Clientes Mensuales
              </h2>
              <p className="text-xs text-gray-500">
                Registra el nº de clientes activos cada mes
              </p>
            </div>
          </div>

          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-lg bg-white"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700 mb-4">
          <strong>Replicación automática:</strong> Si introduces un valor en un
          mes, se replica automáticamente a los meses siguientes (en gris). Para
          cambiar, edita el mes específico.
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-2 font-medium text-gray-500 w-24">
                  Mes
                </th>
                {MONTHS_ABBR.map((m, i) => (
                  <th
                    key={i}
                    className="text-center py-2 px-1 font-medium text-gray-400 w-14"
                  >
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 pr-2 font-medium text-gray-700">
                  Clientes
                </td>
                {Array.from({ length: 12 }, (_, i) => {
                  const month = i + 1;
                  const hasLocal = localClientes[month] !== undefined;
                  const { value, isReplicated } = getDisplayValue(month);
                  const showAsReplicated = isReplicated && !hasLocal;
                  return (
                    <td key={month} className="py-2 px-0.5">
                      <input
                        type="number"
                        min="0"
                        value={
                          hasLocal
                            ? localClientes[month]
                            : showAsReplicated
                              ? ""
                              : value || ""
                        }
                        onChange={(e) => handleChange(month, e.target.value)}
                        placeholder={showAsReplicated ? String(value) : ""}
                        className={`${inputClass} ${showAsReplicated ? "text-gray-400 italic placeholder:text-gray-400" : ""}`}
                      />
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors"
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
